'use client';

import { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

interface TutorialModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
}

export function TutorialModal({ isOpen, onClose }: TutorialModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Pause video and reset when modal closes
  useEffect(() => {
    if (!isOpen && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Tutorial de boas-vindas"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[rgba(15,10,21,0.92)] backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative z-10 flex w-full max-w-4xl flex-col mx-4 max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-brand-dark/95 shadow-2xl animate-scaleIn">
        {/* Close button (floating over video) */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white/80 transition-all hover:bg-black/70 hover:text-white backdrop-blur-sm"
          aria-label="Fechar tutorial"
        >
          <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
        </button>

        {/* Video */}
        <div className="relative w-full bg-black">
          <video
            ref={videoRef}
            className="w-full h-auto max-h-[85vh] object-contain"
            src="/videos/artesturbotutorial.mp4"
            controls
            autoPlay
            playsInline
            onEnded={onClose}
          >
            Seu navegador não suporta vídeos.
          </video>
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
